"use client"

// Conversation-history state for the chat shell.
//
// Wraps a `SessionStore` (the swap-ready persistence seam) and exposes the
// small surface the chat UI needs: the sidebar's list of summaries, the active
// conversation id, and operations to start/open/persist/delete. The component
// tree depends on this hook, not on the storage backend — replacing
// localStorage with the future Memory-backed store changes only the store
// factory call below.

import { useCallback, useEffect, useRef, useState } from "react"
import type { ChatSession, Message, SessionSummary } from "@/components/chat/types"
import type { SessionStore } from "@/services/sessionStore"
import { copy } from "@/lib/copy"

// Longest title kept for the sidebar; longer first-prompts are truncated with
// an ellipsis so list rows stay single-line.
const MAX_TITLE_LENGTH = 48

/**
 * Derive a human-identifiable title from a transcript: the first user prompt,
 * trimmed and truncated. Falls back to a localized "untitled" label when no
 * user text is present yet.
 *
 * @param messages - the conversation transcript.
 * @returns a short title for the sidebar list.
 */
function deriveTitle(messages: Message[]): string {
  const firstUser = messages.find(m => m.role === "user")
  const text = firstUser?.content.trim() ?? ""
  if (!text) return copy.sidebar.untitled
  return text.length > MAX_TITLE_LENGTH ? `${text.slice(0, MAX_TITLE_LENGTH)}…` : text
}

/** What `useSessions` returns to the chat shell. */
export interface UseSessionsResult {
  /** The owner's saved conversations, most-recently-updated first. */
  summaries: SessionSummary[]
  /** The id of the conversation currently shown in the thread. */
  activeId: string
  /**
   * Begin a fresh conversation. Generates and activates a new id; the new
   * conversation is not persisted until it has at least one message.
   *
   * @returns the new active session id.
   */
  startNew: () => string
  /**
   * Open a saved conversation and make it active.
   *
   * @param id - the session id to open.
   * @returns the conversation's transcript (empty if not found).
   */
  open: (id: string) => Promise<Message[]>
  /**
   * Persist the active conversation's transcript, refreshing the summary list.
   * A no-op for an empty transcript (an unused new chat is never saved).
   *
   * @param messages - the current transcript of the active conversation.
   */
  persist: (messages: Message[]) => Promise<void>
  /**
   * Delete a saved conversation and refresh the summary list.
   *
   * @param id - the session id to delete.
   */
  remove: (id: string) => Promise<void>
  /**
   * Rename a saved conversation to a user-chosen title and refresh the list.
   *
   * @param id - the session id to rename.
   * @param title - the new title (empty/whitespace is ignored).
   */
  rename: (id: string, title: string) => Promise<void>
  /**
   * Bump the given conversation's recency so it rises to the top of the
   * sidebar, then refresh the list. Called on turn completion. Best-effort: a
   * failure is logged and swallowed so a recency bump never surfaces to the
   * user or breaks the turn.
   *
   * @param id - the session id to touch (the active id at turn-completion time).
   */
  touch: (id: string) => Promise<void>
  /**
   * Show a just-started conversation in the sidebar immediately, before it has
   * been committed to the store. A brand-new conversation isn't in `list()`
   * until its first message is persisted (cloud: committed to Memory), so
   * without this a new chat is invisible in the sidebar until its first turn
   * finishes. The optimistic row is reconciled by id on the next `refresh()`
   * (the real summary replaces it — no duplicate), and dropped if the turn is
   * abandoned without ever persisting.
   *
   * @param summary - a synthesized summary for the in-flight conversation.
   */
  addOptimistic: (summary: SessionSummary) => void
  /**
   * Reconcile an optimistic row after its turn finishes: stop treating the id
   * as optimistic and re-list, so a committed conversation shows its real
   * summary and one that never persisted (a background turn in the local store)
   * drops out. Best-effort.
   *
   * @param id - the finished turn's session id.
   */
  reconcileOptimistic: (id: string) => Promise<void>
}

/**
 * Manage per-user conversation history for the chat shell.
 *
 * @param store - the persistence backend (local or remote), chosen by the
 *   caller via `createSessionStore`. Injected so the hook is environment- and
 *   test-agnostic.
 * @returns the session list, active id, and history operations.
 */
export function useSessions(store: SessionStore): UseSessionsResult {
  const [summaries, setSummaries] = useState<SessionSummary[]>([])
  // A brand-new conversation id on first mount mirrors the prior behavior of
  // opening to a fresh chat; saved conversations are still listed below it.
  const [activeId, setActiveId] = useState<string>(() => crypto.randomUUID())

  // Optimistically-inserted rows (id → summary) for conversations shown in the
  // sidebar before the store lists them. Kept in a ref so `refresh` can merge
  // them without itself depending on state. A row is retained only until the
  // real store list includes that id (then the real summary wins), so a
  // committed conversation is never doubled.
  const optimisticRef = useRef<Map<string, SessionSummary>>(new Map())

  const refresh = useCallback(async (): Promise<void> => {
    const listed = await store.list()
    const listedIds = new Set(listed.map(s => s.id))
    // Drop any optimistic row the store now knows about (the real one wins),
    // and keep those still absent (their first turn hasn't committed yet).
    for (const id of optimisticRef.current.keys()) {
      if (listedIds.has(id)) optimisticRef.current.delete(id)
    }
    const pending = [...optimisticRef.current.values()].filter(s => !listedIds.has(s.id))
    setSummaries([...pending, ...listed])
  }, [store])

  const addOptimistic = useCallback((summary: SessionSummary): void => {
    optimisticRef.current.set(summary.id, summary)
    setSummaries(prev => {
      if (prev.some(s => s.id === summary.id)) return prev
      return [summary, ...prev]
    })
  }, [])

  const reconcileOptimistic = useCallback(
    async (id: string): Promise<void> => {
      // Called when a turn finishes: stop treating this id as optimistic and
      // re-list. If it committed, the real summary replaces the provisional
      // row; if it never persisted (e.g. a background turn in the local store),
      // it simply drops out. Best-effort — a failed list must not surface.
      // (If `refresh` rejects here, the provisional row can linger in state
      // while no longer tracked as optimistic; it self-heals on the next
      // successful refresh — dropped if still uncommitted, replaced if not.)
      optimisticRef.current.delete(id)
      try {
        await refresh()
      } catch (err) {
        console.warn("Failed to reconcile conversation list:", err)
      }
    },
    [refresh]
  )

  // Load (and reload when the owner changes) the existing history.
  useEffect(() => {
    void refresh()
  }, [refresh])

  const startNew = useCallback((): string => {
    const id = crypto.randomUUID()
    setActiveId(id)
    return id
  }, [])

  const open = useCallback(
    async (id: string): Promise<Message[]> => {
      const session = await store.get(id)
      setActiveId(id)
      // Backfill a stable id on any restored message that lacks one. Cloud
      // transcripts come from Memory events with no client id; local ones may
      // predate the id field. Ids need only be unique within this array (they
      // are the React key and the stream-writer target), not stable across
      // reopens — so a fresh uuid per open is fine.
      return (session?.messages ?? []).map(m => (m.id ? m : { ...m, id: crypto.randomUUID() }))
    },
    [store]
  )

  const persist = useCallback(
    async (messages: Message[]): Promise<void> => {
      if (messages.length === 0) return
      // Preserve the original creation time across updates so ordering and any
      // future "created" display stay stable; only `updatedAt` advances. The
      // title is derived once, on first save, then kept — so a user's rename is
      // not clobbered by re-deriving from the first prompt on the next turn.
      const existing = await store.get(activeId)
      const now = new Date().toISOString()
      const session: ChatSession = {
        id: activeId,
        title: existing?.title ?? deriveTitle(messages),
        messages,
        createdAt: existing?.createdAt ?? now,
        updatedAt: now,
      }
      await store.save(session)
      await refresh()
    },
    [store, activeId, refresh]
  )

  const remove = useCallback(
    async (id: string): Promise<void> => {
      optimisticRef.current.delete(id)
      await store.remove(id)
      await refresh()
    },
    [store, refresh]
  )

  const rename = useCallback(
    async (id: string, title: string): Promise<void> => {
      const trimmed = title.trim()
      if (!trimmed) return
      await store.rename(id, trimmed)
      await refresh()
    },
    [store, refresh]
  )

  const touch = useCallback(
    async (id: string): Promise<void> => {
      // Best-effort recency bump: a transport failure (offline, expired token)
      // must never surface to the user or reject into the turn-completion
      // effect, so swallow it here and only log. The next successful turn re-
      // touches, and the sidebar order self-heals.
      try {
        await store.touch(id)
        await refresh()
      } catch (err) {
        console.warn("Failed to bump conversation recency:", err)
      }
    },
    [store, refresh]
  )

  return {
    summaries,
    activeId,
    startNew,
    open,
    persist,
    remove,
    rename,
    touch,
    addOptimistic,
    reconcileOptimistic,
  }
}
